import type {
  ActionItem,
  ActivityEntry,
  ArchSubmission,
  ChecklistItem,
  CommunityJurisdiction,
  KnowledgeCitation,
  KnowledgeResources,
  KnowledgeSource,
  KnowledgeSourceDetail,
  KnowledgeSourceFilter,
  ProjectType,
  Attachment,
  BoardHubSummary,
  BulkImportJob,
  BulkImportRow,
  Community,
  CommunityEvent,
  CommunityPublicInfo,
  CommunityStats,
  FinancialProjection,
  FinancialSnapshot,
  BylawChat,
  BylawDocument,
  BylawMessage,
  Guideline,
  Issue,
  MemberInvite,
  Newsletter,
  Role,
  User,
  Violation,
  ViolationTag,
} from '@shared/types';

import type {
  CollectionsQuery,
  CollectionsView,
  FinDashboard,
  FinProjectsPayload,
  FinReport,
  FinReportDetail,
  FinSettingsPayload,
  LedgerPage,
  ProjectCandidate,
  VendorDetail,
  VendorsView,
} from './financeTypes';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: Record<string, string[]>,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const isForm = init.body instanceof FormData;
  const response = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const body = payload as { error?: string; details?: Record<string, string[]> } | null;
    throw new ApiError(response.status, body?.error ?? fallbackMessage(response.status), body?.details);
  }
  return payload as T;
}

/** For responses with no JSON body, such as a gateway or function timeout page. */
function fallbackMessage(status: number): string {
  if (status === 502 || status === 504) {
    return 'The server took too long to respond. Please try again, or ask a shorter question.';
  }
  if (status === 503) return 'The service is unavailable right now. Please try again in a moment.';
  return 'Something went wrong. Please try again.';
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
/**
 * On the live site a chat question is answered by a background function, so
 * the server replies with a job id and the page polls until the answer is saved.
 */
type ChatReply = { messages: BylawMessage[] } | { jobId: string };
type StartChatReply =
  | { chatId: string; chats: BylawChat[]; messages: BylawMessage[] }
  | { chatId: string; jobId: string };
type ChatJobPoll =
  | { status: 'pending' }
  | { status: 'failed'; code: number; error: string }
  | { status: 'done'; chatId: string; messages: BylawMessage[]; chats: BylawChat[] };

const CHAT_POLL_MS = 2_500;
/** A little longer than the server's own limit for a background answer. */
const CHAT_WAIT_MS = 5 * 60_000;

async function awaitChatJob(base: '/bylaws' | '/architecture', jobId: string) {
  const deadline = Date.now() + CHAT_WAIT_MS;
  let misses = 0;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, CHAT_POLL_MS));
    let poll: ChatJobPoll;
    try {
      poll = await get<ChatJobPoll>(`${base}/chats/jobs/${jobId}`);
      misses = 0;
    } catch (error) {
      // A dropped connection or a busy server is not the answer failing; keep waiting.
      const transient = !(error instanceof ApiError) || error.status >= 500;
      if (transient && ++misses < 5) continue;
      throw error;
    }
    if (poll.status === 'done') return poll;
    if (poll.status === 'failed') throw new ApiError(poll.code, poll.error);
  }
  throw new ApiError(504, 'That question took too long to answer. Try a shorter question, or ask about one topic at a time.');
}

const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

export type AiProvider = 'gemini' | 'anthropic' | 'openai';

export interface AiProviderSettings {
  label: string;
  baseUrl: string;
  defaultBaseUrl: string;
  model: string;
  defaultModel: string;
  models: string[];
  /** Never the key itself: a masked hint, or the environment variable it comes from. */
  apiKey: { source: 'saved' | 'environment' | 'none'; hint: string | null; envName: string | null };
}

export interface AiSettings {
  provider: AiProvider;
  providers: Record<AiProvider, AiProviderSettings>;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface AiSettingsInput {
  provider: AiProvider;
  baseUrl?: string;
  model?: string;
  apiKey?: string;
  clearApiKey?: boolean;
}

export type AiTestResult = {
  provider: AiProvider;
  model: string;
  ms: number;
} & ({ ok: true; reply: string } | { ok: false; kind: string; error: string });

export interface DirectoryEntry {
  id: string;
  name: string;
  unit: string | null;
  roles: Role[];
  avatarUrl: string | null;
}

export interface VisitorAnalytics {
  days: number;
  since: string;
  totals: {
    visits: number;
    visitors: number;
    returningVisits: number;
    newVisitors: number;
    signedInVisits: number;
  };
  byDay: Array<{ day: string; visits: number; visitors: number }>;
  byCountry: Array<{ countryCode: string; countryName: string; visits: number; visitors: number }>;
  byRegion: Array<{ region: string; countryName: string; visitors: number }>;
  byCity: Array<{ city: string; region: string; countryName: string; visitors: number }>;
  byDevice: Array<{ deviceType: string; visits: number; visitors: number }>;
  byBrowser: Array<{ browser: string; visitors: number }>;
  byOs: Array<{ os: string; visitors: number }>;
  recent: Array<{
    occurredAt: string;
    path: string;
    ip: string | null;
    city: string | null;
    region: string | null;
    countryName: string | null;
    countryCode: string | null;
    deviceType: string;
    os: string | null;
    browser: string | null;
    language: string | null;
    screen: string | null;
    latitude: number | null;
    longitude: number | null;
    timezone: string | null;
    isReturning: boolean;
    sessionCount: number;
    firstSeenAt: string;
    lastSeenAt: string;
    userAgent: string | null;
    referrer: string | null;
    userName: string | null;
    userEmail: string | null;
  }>;
}

export interface AppAnalytics {
  days: number;
  since: string;
  today: string;
  dau: number;
  mau: number;
  stickiness: number;
  dauAllAccounts: number;
  mauAllAccounts: number;
  byDay: Array<{ day: string; dau: number }>;
  logins: Array<{
    occurredAt: string;
    path: string;
    ip: string | null;
    name: string;
    email: string;
    roles: string[];
    communityName: string | null;
  }>;
  pages: Array<{ path: string; feature: string; label: string; views: number; users: number }>;
  features: Array<{ feature: string; label: string; uses: number; users: number }>;
  users: Array<{
    id: string;
    name: string;
    email: string;
    roles: string[];
    communityName: string | null;
    lastSeenAt: string;
    daysActive: number;
    eventCount: number;
  }>;
  communities: Array<{ id: string; name: string; dau: number; mau: number }>;
}

export const api = {
  /* ---------------------------------------------------------------- auth */
  me: () => get<{ user: User | null; community: Community | null }>('/me'),
  login: (email: string, password: string) =>
    post<{ user: User; community: Community | null }>('/login', { email, password }),
  signup: (input: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    unit: string;
    phone: string;
    inviteCode: string;
    emailConsent: true;
  }) => post<{ user: User; community: Community | null }>('/signup', input),
  logout: () => post<{ ok: true }>('/logout'),
  forgotPassword: (email: string) =>
    post<{ ok: true; message: string }>('/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    post<{ user: User; community: Community | null }>('/reset-password', { token, password }),
  updateProfile: (input: {
    name?: string;
    firstName?: string;
    lastName?: string;
    unit?: string | null;
    phone?: string | null;
    avatarAttachmentId?: string | null;
  }) => patch<{ user: User }>('/me', input),
  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ ok: true }>('/me/password', { currentPassword, newPassword }),
  directory: () => get<{ users: DirectoryEntry[] }>('/directory'),
  residents: (params?: { q?: string; role?: string; resident?: string; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.q) query.set('q', params.q);
    if (params?.role) query.set('role', params.role);
    if (params?.resident) query.set('resident', params.resident);
    if (params?.status) query.set('status', params.status);
    const suffix = query.toString() ? `?${query}` : '';
    return get<{ users: User[]; total: number }>(`/users${suffix}`);
  },
  setRoles: (
    id: string,
    roles: Role[],
    extra?: {
      approved?: boolean;
      isTenant?: boolean;
      membershipStatus?: 'active' | 'suspended';
      firstName?: string | null;
      lastName?: string | null;
      unit?: string | null;
      phone?: string | null;
    },
  ) => patch<{ user: User }>(`/users/${id}/roles`, { roles, ...extra }),
  invites: () => get<{ invites: MemberInvite[] }>('/invites'),
  createInvite: (input: {
    roles: Role[];
    note?: string | null;
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    unit: string;
    maxUses?: number;
    expiresInDays?: number;
    isTenant?: boolean;
  }) => post<{ code: string; emailed: boolean; mailError?: string | null }>('/invites', input),
  resendInvite: (code: string) =>
    post<{ code: string; emailed: boolean; mailError?: string | null }>(`/invites/${code}/resend`),
  revokeInvite: (code: string) => post<{ ok: true }>(`/invites/${code}/revoke`),
  deleteInvite: (code: string) => del<{ ok: true }>(`/invites/${code}`),
  communityPublic: () => get<{ info: CommunityPublicInfo }>('/community/public'),

  /* ------------------------------------------------------------ community */
  community: () => get<{ community: Community | null }>('/community'),
  updateCommunity: (input: Record<string, unknown>) =>
    patch<{ community: Community }>('/community', input),
  adminCommunities: () => get<{ communities: CommunityStats[] }>('/admin/communities'),
  adminCommunity: (id: string) => get<{ stats: CommunityStats }>(`/admin/communities/${id}`),
  adminCreateCommunity: (input: Record<string, unknown>) =>
    post<{
      community: Community;
      user: User;
      invitedEmail: string;
      emailed: boolean;
      mailError: string | null;
      code: string | null;
    }>('/admin/communities', input),
  adminCommunityOptions: () =>
    get<{ communities: { id: string; name: string; status: 'active' | 'disabled' }[] }>(
      '/admin/community-options',
    ),
  /** Choose the community a Super Admin manages; `null` returns to the platform view. */
  adminActInCommunity: (communityId: string | null) =>
    post<{ user: User; community: Community | null }>('/admin/acting-community', { communityId }),
  adminJoinCommunity: (id: string) =>
    post<{ community: Community; user: User }>(`/admin/communities/${id}/join`),
  adminUpdateCommunity: (id: string, input: Record<string, unknown>) =>
    patch<{ community: Community }>(`/admin/communities/${id}`, input),
  adminCommunityDetail: (id: string) =>
    get<{
      stats: CommunityStats;
      primaryContact: { id: string; name: string; email: string; phone: string | null } | null;
      boardInvite: {
        code: string;
        email: string | null;
        firstName: string | null;
        lastName: string | null;
        phone: string | null;
        status: string;
        deliveryError: string | null;
      } | null;
    }>(`/admin/communities/${id}/detail`),
  adminSaveBoardMember: (
    id: string,
    input: { name: string; email: string; phone: string; resend?: boolean },
  ) =>
    patch<{ community: Community; emailed: boolean; mailError: string | null; code: string | null }>(
      `/admin/communities/${id}/board-member`,
      input,
    ),
  adminResendBoardInvite: (id: string) =>
    post<{ code: string; emailed: boolean; mailError: string | null }>(
      `/admin/communities/${id}/board-member/resend`,
    ),
  adminDisableCommunity: (id: string, reason?: string | null) =>
    post<{ community: Community }>(`/admin/communities/${id}/disable`, {
      reason: reason ?? null,
      confirm: true,
    }),
  adminEnableCommunity: (id: string) =>
    post<{ community: Community }>(`/admin/communities/${id}/enable`),
  adminSetPrimaryContact: (id: string, userId: string) =>
    post<{ community: Community }>(`/admin/communities/${id}/primary-contact`, { userId }),
  adminBoardInvite: (communityId: string, input?: { note?: string | null; maxUses?: number; expiresInDays?: number }) =>
    post<{ code: string; communityId: string; roles: Role[] }>(
      `/admin/communities/${communityId}/board-invites`,
      input ?? {},
    ),
  adminSuperInvite: (input?: { note?: string | null }) =>
    post<{ code: string; roles: Role[] }>('/admin/superadmin-invites', input ?? {}),
  analyticsVisit: (input: { path: string; referrer?: string | null; language?: string | null; screen?: string | null }) =>
    post<{ ok: true; returning: boolean }>('/analytics/visit', input),
  analyticsPage: (path: string, title?: string | null) =>
    post<{ ok: true }>('/analytics/page', { path, title: title ?? null }),
  adminVisitorAnalytics: (days = 30) => get<VisitorAnalytics>(`/admin/analytics/visitors?days=${days}`),
  adminAppAnalytics: (days = 30) => get<AppAnalytics>(`/admin/analytics/usage?days=${days}`),
  adminAiSettings: () => get<AiSettings>('/admin/ai-settings'),
  adminSaveAiSettings: (input: AiSettingsInput) => put<AiSettings>('/admin/ai-settings', input),
  adminTestAiSettings: (input: AiSettingsInput) => post<AiTestResult>('/admin/ai-settings/test', input),

  /* --------------------------------------------------------------- imports */
  importHistory: (communityId?: string) =>
    get<{ jobs: BulkImportJob[] }>(
      `/imports${communityId ? `?communityId=${encodeURIComponent(communityId)}` : ''}`,
    ),
  importPreview: (file: File, communityId?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (communityId) form.append('communityId', communityId);
    return request<{ job: BulkImportJob; rows: BulkImportRow[] }>('/imports/preview', {
      method: 'POST',
      body: form,
    });
  },
  importJob: (id: string) => get<{ job: BulkImportJob; rows: BulkImportRow[] }>(`/imports/${id}`),
  importCommit: (id: string, sendInvitations = true) =>
    post<{ ok: true; imported: number; invited: number }>(`/imports/${id}/commit`, {
      sendInvitations,
      onlyValid: true,
    }),

  /* ----------------------------------------------------------- violations */
  violationTags: () => get<{ tags: ViolationTag[] }>('/violations/tags'),
  createViolationTag: (name: string) =>
    post<{ tag: ViolationTag; tags: ViolationTag[] }>('/violations/tags', { name }),
  deleteViolationTag: (id: string) => del<{ tags: ViolationTag[] }>(`/violations/tags/${id}`),
  violations: (status?: string) =>
    get<{ violations: Violation[] }>(`/violations${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  violationCreate: (input: Record<string, unknown>) =>
    post<{ violation: Violation }>('/violations', input),
  violationUpdate: (id: string, status: string) =>
    patch<{ violation: Violation }>(`/violations/${id}`, { status }),
  violationAddNote: (id: string, body: string, internal: boolean, attachmentIds: string[]) =>
    post<{ violation: Violation }>(`/violations/${id}/notes`, { body, internal, attachmentIds }),
  violationAddFiles: (id: string, attachmentIds: string[]) =>
    post<{ violation: Violation }>(`/violations/${id}/attachments`, { attachmentIds }),

  /* ------------------------------------------------------------- uploads */
  upload: (files: File[]) => {
    const form = new FormData();
    for (const file of files) form.append('files', file);
    return request<{ attachments: Attachment[] }>('/uploads', { method: 'POST', body: form });
  },
  deleteUpload: (id: string) => del<{ ok: true }>(`/uploads/${id}`),

  /* -------------------------------------------------------- architecture */
  archList: (status?: string) =>
    get<{ submissions: ArchSubmission[] }>(`/architecture${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  archGet: (id: string) => get<{ submission: ArchSubmission }>(`/architecture/${id}`),
  archCreate: (input: Record<string, unknown>) =>
    post<{ submission: ArchSubmission }>('/architecture', input),
  archUpdate: (id: string, input: Record<string, unknown>) =>
    patch<{ submission: ArchSubmission }>(`/architecture/${id}`, input),
  archSetStatus: (id: string, status: string, reason?: string, citations?: KnowledgeCitation[]) =>
    post<{ submission: ArchSubmission; mailError: string | null }>(`/architecture/${id}/status`, { status, reason, citations }),
  archSubmit: (id: string) =>
    post<{ submission: ArchSubmission; mailError: string | null }>(`/architecture/${id}/submit`),
  archVote: (id: string, vote: 'approve' | 'deny', comment?: string) =>
    post<{ submission: ArchSubmission }>(`/architecture/${id}/vote`, { vote, comment }),
  archAddNote: (id: string, body: string, internal: boolean, attachmentIds: string[], citations?: KnowledgeCitation[]) =>
    post<{ submission: ArchSubmission }>(`/architecture/${id}/notes`, { body, internal, attachmentIds, citations }),
  archAddFiles: (id: string, attachmentIds: string[]) =>
    post<{ submission: ArchSubmission }>(`/architecture/${id}/attachments`, { attachmentIds }),
  archAiReview: (id: string, engine?: 'rules') =>
    post<{ submission: ArchSubmission }>(`/architecture/${id}/ai-review${engine ? `?engine=${engine}` : ''}`),

  /* ---------------------------------------------------------- guidelines */
  guidelines: () => get<{ guidelines: Guideline[]; aiEngine: 'gemini' | 'rules' }>('/guidelines'),
  guidelineCreate: (input: Record<string, unknown>) =>
    post<{ guidelines: Guideline[] }>('/guidelines', input),
  guidelineUpdate: (id: string, input: Record<string, unknown>) =>
    put<{ guidelines: Guideline[] }>(`/guidelines/${id}`, input),
  guidelineDelete: (id: string) => del<{ guidelines: Guideline[] }>(`/guidelines/${id}`),
  guidelineImport: (guidelines: Record<string, unknown>[], replaceAll: boolean) =>
    post<{ guidelines: Guideline[] }>('/guidelines/import', { guidelines, replaceAll }),

  /* -------------------------------------------------------------- issues */
  issues: (status?: string) =>
    get<{ issues: Issue[] }>(`/issues${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  issueCreate: (input: Record<string, unknown>) => post<{ issue: Issue }>('/issues', input),
  issueUpdate: (id: string, input: Record<string, unknown>) =>
    patch<{ issue: Issue }>(`/issues/${id}`, input),
  issueAssign: (id: string, userId: string | null) =>
    post<{ issue: Issue }>(`/issues/${id}/assign`, { userId }),
  issueAddNote: (id: string, body: string, internal: boolean, attachmentIds: string[]) =>
    post<{ issue: Issue }>(`/issues/${id}/notes`, { body, internal, attachmentIds }),
  issueAddFiles: (id: string, attachmentIds: string[]) =>
    post<{ issue: Issue }>(`/issues/${id}/attachments`, { attachmentIds }),

  /* -------------------------------------------------------------- events */
  events: (scope: 'upcoming' | 'past' | 'all' = 'upcoming') =>
    get<{ events: CommunityEvent[] }>(`/events?scope=${scope}`),
  eventCreate: (input: Record<string, unknown>) => post<{ event: CommunityEvent }>('/events', input),
  eventUpdate: (id: string, input: Record<string, unknown>) =>
    put<{ event: CommunityEvent }>(`/events/${id}`, input),
  eventDelete: (id: string) => del<{ ok: true }>(`/events/${id}`),
  eventRsvp: (id: string, status: 'going' | 'maybe' | 'not_going' | null, guests = 0) =>
    post<{ event: CommunityEvent }>(`/events/${id}/rsvp`, { status, guests }),
  eventComment: (id: string, body: string, attachmentIds: string[] = []) =>
    post<{ event: CommunityEvent }>(`/events/${id}/notes`, { body, attachmentIds }),

  /* ----------------------------------------------------------- board hub */
  activity: (limit = 25) => get<{ activity: ActivityEntry[] }>(`/activity?limit=${limit}`),
  boardSummary: () => get<{ summary: BoardHubSummary }>('/board/summary'),
  actionItems: () => get<{ actionItems: ActionItem[] }>('/action-items'),
  actionCreate: (input: Record<string, unknown>) =>
    post<{ actionItems: ActionItem[] }>('/action-items', input),
  actionUpdate: (id: string, input: Record<string, unknown>) =>
    patch<{ actionItems: ActionItem[] }>(`/action-items/${id}`, input),
  actionDelete: (id: string) => del<{ actionItems: ActionItem[] }>(`/action-items/${id}`),

  /* ---------------------------------------------------------- financials */
  financeCurrent: () =>
    get<{ snapshot: FinancialSnapshot | null; projection: FinancialProjection | null }>('/finance/current'),
  financeSnapshots: () =>
    get<{
      snapshots: { id: string; year: number; throughMonth: number; label: string; uploadedByName: string; createdAt: string }[];
    }>('/finance/snapshots'),
  financeSnapshot: (id: string) =>
    get<{ snapshot: FinancialSnapshot; projection: FinancialProjection }>(`/finance/snapshots/${id}`),
  financeSave: (input: Record<string, unknown>) =>
    post<{ snapshot: FinancialSnapshot; projection: FinancialProjection }>('/finance/snapshots', input),
  financeDelete: (id: string) => del<{ ok: true }>(`/finance/snapshots/${id}`),
  financeParse: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{
      filename: string;
      parsed: {
        delinquents: { unit: string; ownerName: string | null; amount: number; daysPastDue: number; inCollections: boolean }[];
        vendors: { vendor: string; category: string | null; amount: number }[];
        monthly: { month: number; income: number; expenses: number }[];
        warnings: string[];
      };
    }>('/finance/parse', { method: 'POST', body: form });
  },

  /* ----------------------------------------------- financial intelligence */
  finDashboard: (granularity: string, fund: string) =>
    get<FinDashboard>(`/finance/dashboard?granularity=${granularity}&fund=${fund}`),
  finReports: () => get<{ reports: FinReport[] }>('/finance/reports'),
  finUpload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ report: FinReport }>('/finance/reports', { method: 'POST', body: form });
  },
  finReport: (id: string) => get<FinReportDetail>(`/finance/reports/${id}`),
  finLedger: (id: string, kind: 'gl' | 'ap' | 'ar', q: string, offset: number) =>
    get<LedgerPage<Record<string, string | number | boolean | null>>>(
      `/finance/reports/${id}/ledger/${kind}?q=${encodeURIComponent(q)}&offset=${offset}&limit=50`,
    ),
  finSetPeriod: (id: string, input: { periodYear: number; periodMonth: number; confirm: boolean }) =>
    patch<{ ok: true }>(`/finance/reports/${id}`, input),
  finCorrect: (
    id: string,
    input: { recordType: 'pl' | 'balance'; recordId: string; field: string; value: string; reason: string },
  ) => post<{ ok: true }>(`/finance/reports/${id}/corrections`, input),
  finApprove: (id: string, input: { confirmPeriod: true; acknowledgeErrors: boolean; note?: string }) =>
    post<{ ok: true; superseded: number }>(`/finance/reports/${id}/approve`, input),
  finReprocess: (id: string) => post<{ ok: true }>(`/finance/reports/${id}/reprocess`),
  finReject: (id: string, reason: string) => post<{ ok: true }>(`/finance/reports/${id}/reject`, { reason }),
  finAnnotate: (id: string, issueCode: string, note: string) =>
    post<{ ok: true }>(`/finance/reports/${id}/annotations`, { issueCode, note }),
  finDeleteReport: (id: string) => del<{ ok: true }>(`/finance/reports/${id}`),
  finDownloadLink: (id: string) =>
    post<{ url: string; expiresInSeconds: number }>(`/finance/reports/${id}/download-link`),
  finReportExportUrl: (id: string) => `/api/finance/reports/${id}/export`,
  finCollections: (query: CollectionsQuery) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') params.set(key, String(value));
    }
    return get<CollectionsView>(`/finance/collections?${params.toString()}`);
  },
  finCollectionsExportUrl: (query: CollectionsQuery) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries({ ...query, offset: undefined, limit: undefined })) {
      if (value !== undefined && value !== '') params.set(key, String(value));
    }
    return `/api/finance/collections/export?${params.toString()}`;
  },
  finVendors: (report?: string) => get<VendorsView>(`/finance/vendors${report ? `?report=${report}` : ''}`),
  finVendor: (id: string, report?: string) =>
    get<VendorDetail>(`/finance/vendors/${id}${report ? `?report=${report}` : ''}`),
  finVendorUpdate: (
    id: string,
    input: { categoryId?: string | null; recurring?: boolean | null; contractMonthly?: number | null; name?: string },
  ) => patch<{ ok: true }>(`/finance/vendors/${id}`, input),
  finVendorMerge: (id: string, intoId: string) =>
    post<{ ok: true }>(`/finance/vendors/${id}/merge`, { intoId }),
  finSettings: () => get<FinSettingsPayload>('/finance/settings'),
  finSettingsSave: (input: Record<string, unknown>) => put<FinSettingsPayload>('/finance/settings', input),
  finSettingsConfirm: () => post<FinSettingsPayload>('/finance/settings/confirm'),
  finRateSave: (input: Record<string, unknown>) => post<FinSettingsPayload>('/finance/settings/rates', input),
  finRateDelete: (id: string) => del<FinSettingsPayload>(`/finance/settings/rates/${id}`),
  finContractSave: (input: Record<string, unknown>) =>
    post<FinSettingsPayload>('/finance/settings/contracts', input),
  finContractDelete: (id: string) => del<FinSettingsPayload>(`/finance/settings/contracts/${id}`),
  finCategoryAdd: (name: string) => post<FinSettingsPayload>('/finance/categories', { name }),
  finCategoryDelete: (id: string) => del<FinSettingsPayload>(`/finance/categories/${id}`),
  finProjects: () => get<FinProjectsPayload>('/finance/projects'),
  finProjectSave: (input: Record<string, unknown>, id?: string) =>
    id ? put<FinProjectsPayload>(`/finance/projects/${id}`, input) : post<FinProjectsPayload>('/finance/projects', input),
  finProjectDelete: (id: string) => del<FinProjectsPayload>(`/finance/projects/${id}`),
  finProjectExpense: (id: string, input: Record<string, unknown>) =>
    post<FinProjectsPayload>(`/finance/projects/${id}/expenses`, input),
  finProjectExpenseDelete: (id: string, expenseId: string) =>
    del<FinProjectsPayload>(`/finance/projects/${id}/expenses/${expenseId}`),
  finProjectCandidates: () => get<{ entries: ProjectCandidate[] }>('/finance/projects/candidates'),
  finProjectSchedule: (id: string, input: Record<string, unknown>) =>
    post<FinProjectsPayload>(`/finance/projects/${id}/schedule`, input),
  finProjectScheduleDone: (id: string, itemId: string) =>
    post<FinProjectsPayload>(`/finance/projects/${id}/schedule/${itemId}/done`),
  finProjectScheduleDelete: (id: string, itemId: string) =>
    del<FinProjectsPayload>(`/finance/projects/${id}/schedule/${itemId}`),
  finForecast: (horizon: string) => get<Record<string, unknown>>(`/finance/forecast?horizon=${horizon}`),
  finForecastAssumptions: (scenario: string, overrides: Record<string, number | null>) =>
    put<{ ok: true }>(`/finance/forecast/assumptions/${scenario}`, { overrides }),
  finForecastAssumptionsReset: (scenario: string) => del<{ ok: true }>(`/finance/forecast/assumptions/${scenario}`),
  finScheduledAdd: (input: Record<string, unknown>) =>
    post<{ scheduled: unknown[] }>('/finance/forecast/scheduled', input),
  finScheduledDelete: (id: string) => del<{ scheduled: unknown[] }>(`/finance/forecast/scheduled/${id}`),
  finBudgets: () => get<Record<string, unknown>>('/finance/budgets'),
  finBudgetCreate: (input: { name: string; source: 'approved' | 'actuals' | 'zero'; fiscalYear?: number }) =>
    post<{ id: string }>('/finance/budgets', input),
  finBudgetImport: (file: File, name: string, fiscalYear: number) => {
    const form = new FormData();
    form.append('file', file);
    form.append('name', name);
    form.append('fiscalYear', String(fiscalYear));
    return request<{ id: string; skipped: string[] }>('/finance/budgets/import', { method: 'POST', body: form });
  },
  finBudget: (id: string) => get<Record<string, unknown>>(`/finance/budgets/${id}`),
  finBudgetEvaluate: (scenario: Record<string, unknown>) =>
    post<{ result: Record<string, unknown>; breakEven: Record<string, unknown> }>('/finance/budgets/evaluate', scenario),
  finBudgetDuesComparison: (scenario: Record<string, unknown>, dues: number[]) =>
    post<{ options: Array<Record<string, unknown>> }>('/finance/budgets/dues-comparison', { scenario, dues }),
  finBudgetSave: (id: string, input: Record<string, unknown>) =>
    put<Record<string, unknown>>(`/finance/budgets/${id}`, input),
  finBudgetDuplicate: (id: string, name: string) =>
    post<{ id: string }>(`/finance/budgets/${id}/duplicate`, { name }),
  finBudgetDelete: (id: string) => del<{ ok: true }>(`/finance/budgets/${id}`),
  finBudgetCompare: (ids: string[]) =>
    get<{ scenarios: Array<Record<string, unknown>> }>(`/finance/budgets/compare?ids=${ids.join(',')}`),
  finBudgetExportUrl: (id: string) => `/api/finance/budgets/${id}/export`,
  finInsights: () => get<Record<string, unknown>>('/finance/insights'),
  finInsightExplain: (id: string) =>
    post<{ explanation: string; questions: string[]; ai: boolean; engine?: string; note?: string }>(
      '/finance/insights/explain',
      { id },
    ),
  finAsk: (question: string) => post<Record<string, unknown>>('/finance/ask', { question }),
  finTimeline: () => get<Record<string, unknown>>('/finance/timeline'),
  finCompare: (a: string, b: string) => get<Record<string, unknown>>(`/finance/compare?a=${a}&b=${b}`),

  /* ----------------------------------------------------------- newsletter */
  newsletters: () =>
    get<{ newsletters: Newsletter[]; aiEngine: 'gemini' | 'template' }>('/newsletters'),
  newsletterGenerate: (input: {
    year: number;
    month: number;
    communityName: string;
    extraNotes: string;
    engine: 'gemini' | 'template';
  }) =>
    post<{
      draft: string;
      engine: 'gemini' | 'template';
      title: string;
      context: {
        issueCount: number;
        resolvedCount: number;
        eventCount: number;
        archCount: number;
        hasFinance: boolean;
      };
    }>('/newsletters/generate', input),
  newsletterSave: (input: Record<string, unknown>) =>
    post<{ newsletter: Newsletter }>('/newsletters', input),
  newsletterUpdate: (id: string, input: Record<string, unknown>) =>
    put<{ newsletter: Newsletter }>(`/newsletters/${id}`, input),
  newsletterDelete: (id: string) => del<{ ok: true }>(`/newsletters/${id}`),

  /* -------------------------------------------------------------- bylaws */
  bylaws: () =>
    get<{ documents: BylawDocument[]; aiEngine: 'gemini' | 'none' }>('/bylaws/documents'),
  bylawUpload: (files: File[], title?: string) => {
    const form = new FormData();
    for (const file of files) form.append('files', file);
    if (title?.trim()) form.append('title', title.trim());
    return request<{ documents: BylawDocument[] }>('/bylaws/documents', { method: 'POST', body: form });
  },
  bylawRename: (id: string, title: string) =>
    patch<{ documents: BylawDocument[] }>(`/bylaws/documents/${id}`, { title }),
  bylawReread: (id: string) => post<{ documents: BylawDocument[] }>(`/bylaws/documents/${id}/extract`),
  bylawDelete: (id: string) => del<{ documents: BylawDocument[] }>(`/bylaws/documents/${id}`),
  bylawChats: () => get<{ chats: BylawChat[] }>('/bylaws/chats'),
  bylawChat: (id: string) => get<{ messages: BylawMessage[] }>(`/bylaws/chats/${id}`),
  bylawStartChat: async (message: string) => {
    const reply = await post<StartChatReply>('/bylaws/chats', { message });
    if (!('jobId' in reply)) return reply;
    const done = await awaitChatJob('/bylaws', reply.jobId);
    return { chatId: done.chatId, chats: done.chats, messages: done.messages };
  },
  bylawSend: async (chatId: string, message: string) => {
    const reply = await post<ChatReply>(`/bylaws/chats/${chatId}/messages`, { message });
    if (!('jobId' in reply)) return reply;
    const done = await awaitChatJob('/bylaws', reply.jobId);
    return { messages: done.messages };
  },
  bylawDeleteChat: (id: string) => del<{ chats: BylawChat[] }>(`/bylaws/chats/${id}`),

  archDocs: () =>
    get<{ documents: BylawDocument[]; aiEngine: 'gemini' | 'none' }>('/architecture/documents'),
  archDocUpload: (files: File[], title?: string) => {
    const form = new FormData();
    for (const file of files) form.append('files', file);
    if (title?.trim()) form.append('title', title.trim());
    return request<{ documents: BylawDocument[] }>('/architecture/documents', { method: 'POST', body: form });
  },
  archDocRename: (id: string, title: string) =>
    patch<{ documents: BylawDocument[] }>(`/architecture/documents/${id}`, { title }),
  archDocReread: (id: string) => post<{ documents: BylawDocument[] }>(`/architecture/documents/${id}/extract`),
  archDocDelete: (id: string) => del<{ documents: BylawDocument[] }>(`/architecture/documents/${id}`),
  archChats: () => get<{ chats: BylawChat[] }>('/architecture/chats'),
  archChat: (id: string) => get<{ messages: BylawMessage[] }>(`/architecture/chats/${id}`),
  archStartChat: async (message: string, filter: KnowledgeSourceFilter = 'all') => {
    const reply = await post<StartChatReply>('/architecture/chats', { message, filter });
    if (!('jobId' in reply)) return reply;
    const done = await awaitChatJob('/architecture', reply.jobId);
    return { chatId: done.chatId, chats: done.chats, messages: done.messages };
  },
  archSend: async (chatId: string, message: string, filter: KnowledgeSourceFilter = 'all') => {
    const reply = await post<ChatReply>(`/architecture/chats/${chatId}/messages`, { message, filter });
    if (!('jobId' in reply)) return reply;
    const done = await awaitChatJob('/architecture', reply.jobId);
    return { messages: done.messages };
  },
  archDeleteChat: (id: string) => del<{ chats: BylawChat[] }>(`/architecture/chats/${id}`),
  archDocUpdate: (
    id: string,
    input: { title?: string; visibility?: 'owners' | 'committee'; category?: string; published?: boolean },
  ) => patch<{ documents: BylawDocument[] }>(`/architecture/documents/${id}`, input),

  /* ------------------------------------------ architecture knowledge center */
  archResources: () => get<KnowledgeResources>('/architecture/resources'),
  archJurisdiction: () => get<{ jurisdiction: CommunityJurisdiction }>('/architecture/jurisdiction'),
  archSaveJurisdiction: (input: Record<string, unknown>) =>
    put<{ jurisdiction: CommunityJurisdiction }>('/architecture/jurisdiction', input),
  archSources: (archived = false) =>
    get<{ sources: KnowledgeSource[] }>(`/architecture/sources${archived ? '?archived=1' : ''}`),
  archSource: (id: string) => get<KnowledgeSourceDetail>(`/architecture/sources/${id}`),
  archCreateSource: (input: Record<string, unknown>) =>
    post<{ sources: KnowledgeSource[] }>('/architecture/sources', input),
  archBulkSources: (urls: string[], defaults: Record<string, unknown>) =>
    post<{ results: { url: string; ok: boolean; error?: string }[]; sources: KnowledgeSource[] }>(
      '/architecture/sources/bulk',
      { urls, defaults },
    ),
  archAustinStarter: () => post<{ added: number; sources: KnowledgeSource[] }>('/architecture/sources/starter/austin'),
  archUpdateSource: (id: string, input: Record<string, unknown>) =>
    patch<{ sources: KnowledgeSource[] }>(`/architecture/sources/${id}`, input),
  archSyncSource: (id: string) => post<{ sources: KnowledgeSource[] }>(`/architecture/sources/${id}/sync`),
  archArchiveSource: (id: string, archived: boolean) =>
    post<{ sources: KnowledgeSource[] }>(`/architecture/sources/${id}/archive`, { archived }),
  archPublishSource: (id: string, published: boolean) =>
    post<{ sources: KnowledgeSource[] }>(`/architecture/sources/${id}/publish`, { published }),
  archSourceVersion: (id: string, versionId: string) =>
    get<{ version: number; status: string; finalUrl: string; flags: string[]; pages: { url: string; title: string }[]; text: string; truncated: boolean }>(
      `/architecture/sources/${id}/versions/${versionId}`,
    ),
  archApproveVersion: (id: string, versionId: string) =>
    post<{ sources: KnowledgeSource[] }>(`/architecture/sources/${id}/versions/${versionId}/approve`),
  archRejectVersion: (id: string, versionId: string) =>
    post<{ sources: KnowledgeSource[] }>(`/architecture/sources/${id}/versions/${versionId}/reject`),
  archChecklist: (projectType: ProjectType) =>
    get<{ projectType: ProjectType; items: ChecklistItem[]; custom: boolean }>(
      `/architecture/checklists/${encodeURIComponent(projectType)}`,
    ),
  archSaveChecklist: (projectType: ProjectType, items: ChecklistItem[]) =>
    put<{ projectType: ProjectType; items: ChecklistItem[]; custom: boolean }>(
      `/architecture/checklists/${encodeURIComponent(projectType)}`,
      { items },
    ),
  archDraftFromChat: (input: {
    chatId: string;
    title: string;
    description: string;
    projectType: ProjectType;
    sources: { kind: 'document' | 'source'; sourceId: string; excerpt?: string }[];
  }) => post<{ submission: ArchSubmission }>('/architecture/from-chat', input),
  archReviewers: () => get<{ reviewers: { id: string; name: string }[] }>('/architecture/reviewers'),
  archAssign: (id: string, reviewerIds: string[]) =>
    post<{ submission: ArchSubmission }>(`/architecture/${id}/assign`, { reviewerIds }),
  archResearch: (id: string) => post<{ submission: ArchSubmission }>(`/architecture/${id}/research`),
  archEditResearch: (id: string, noteId: string, body: string) =>
    patch<{ submission: ArchSubmission }>(`/architecture/${id}/research/${noteId}`, { body }),
  archListAssigned: () => get<{ submissions: ArchSubmission[] }>('/architecture?assigned=me'),
};
